/**
 * Air Terminal Tool
 * 
 * Host-based Air Terminal placement with flow calculations.
 */

import { v4 as uuidv4 } from 'uuid';
import { AirTerminalElement, AirTerminalGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export class AirTerminalTool extends BaseTool {
    id = 'air_terminal';
    name = 'Air Terminal';
    cursor = 'crosshair';

    private currentPoint: THREE.Vector3 | null = null;
    private currentNormal: THREE.Vector3 = new THREE.Vector3(0, 0, 1);
    private currentHostId: string | null = null;

    // Default configuration
    private width = 600; // 600x600 diffuser
    private height = 600;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        if (this.currentPoint && this.currentHostId) {
            this.createAirTerminal();
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        // Use HostManager to find a face to host on (Ceiling, WorkPlane, Element Face)
        // For now, simple raycasting or reusing HostManager logic if available
        // In BaseTool, we initialized this.hostManager

        const result = this.hostManager?.findHost(event.intersections, {
            categories: ['Ceilings', 'Floors', 'Mechanical Equipment']
        });

        if (result) {
            this.currentPoint = result.point;
            this.currentNormal = result.normal;
            this.currentHostId = result.elementId;
        } else {
            // Fallback to ground plane or workplane if no host found
            const point = this.getIntersection(event);
            if (point) {
                this.currentPoint = this.snapToGrid(point);
                this.currentNormal = new THREE.Vector3(0, 0, 1);
                this.currentHostId = 'ground-plane';
            } else {
                this.currentPoint = null;
            }
        }

        this.updatePreview();
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.deactivate();
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (!this.currentPoint) {
            this.context.appStore.setToolPreview(null);
            return;
        }

        this.context.appStore.setToolPreview({
            type: 'box',
            position: this.currentPoint,
            size: [this.width / 1000, this.height / 1000, 0.1], // mm to m
            rotation: [0, 0, 0], // Simplified rotation for now
            color: '#00aaff'
        });
    }

    private createAirTerminal() {
        if (!this.context || !this.currentPoint || !this.currentHostId) return;

        const geometryData: AirTerminalGeometryData = {
            position: [this.currentPoint.x, this.currentPoint.y, this.currentPoint.z],
            faceNormal: [this.currentNormal.x, this.currentNormal.y, this.currentNormal.z],
            width: this.width,
            height: this.height
        };

        const flow = 500; // Default CFM
        const neckSize = 250; // mm

        // Simple calculation logic for now
        // Throw roughly proportional to Flow / Neck Area
        const throwDist = (flow / neckSize) * 0.1;

        const airTerminal: AirTerminalElement = {
            id: uuidv4(),
            category: 'Air Terminals',
            type: 'Supply Diffuser 600x600',
            parameters: {
                'Flow': `${flow} CFM`,
                'Neck Size': `${neckSize} mm`,
                'Throw': `${throwDist.toFixed(1)} m`,
                'System Type': 'Supply Air'
            },
            constraints: [
                { type: 'HOST_CONTAINED', targetId: this.currentHostId, data: {} }
            ],
            geometryData
        };

        this.context.bimStore.addElement(airTerminal);

        // Reset or keep placing
        // this.currentHostId = null; // Don't reset to allow rapid placement
    }
}
